import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { convertISODateToDayJsWithTimezone } from 'app/utils';

import type { ApiAppointment, PrintRow } from '../types';
import { printStatus } from './printStatus';

// dayjs utc/timezone plugins are also extended globally in app/initDateTime; extending here keeps
// this helper self-contained for unit tests (same approach as broadcasts/scheduling.ts).
dayjs.extend(utc);
dayjs.extend(timezone);

export const UNASSIGNED_PATIENT_LABEL = 'Unassigned';
export const EMPTY_CELL = '-';
/** 12-hour with AM/PM: what the Calendar screen shows under the en-ca locale (`LT`). */
export const PRINT_TIME_FORMAT = 'h:mm A';
export const DAY_KEY_FORMAT = 'YYYY-MM-DD';

interface FormatPrintRowOptions {
  pharmacyTimezone: string;
  now: Date;
}

/** Same precedence as useAppointmentTimezone, as a plain function so it can run over a list. */
export function resolveAppointmentTimezone(
  appointment: Pick<ApiAppointment, 'timezone'>,
  pharmacyTimezone: string
): string {
  return appointment.timezone || pharmacyTimezone;
}

export function formatPrintRow(
  appointment: ApiAppointment,
  { pharmacyTimezone, now }: FormatPrintRowOptions
): PrintRow {
  const tz = resolveAppointmentTimezone(appointment, pharmacyTimezone);
  const start = convertISODateToDayJsWithTimezone(appointment.startDate, tz);
  const end = convertISODateToDayJsWithTimezone(appointment.endDate, tz);
  const patient = appointment.patientFullName?.trim() ?? '';

  return {
    id: appointment.id,
    dayKey: start.format(DAY_KEY_FORMAT),
    startTime: start.format(PRINT_TIME_FORMAT),
    endTime: end.format(PRINT_TIME_FORMAT),
    patient: patient || UNASSIGNED_PATIENT_LABEL,
    isUnassigned: patient.length === 0,
    service: appointment.serviceName,
    room: appointment.roomName?.trim() || EMPTY_CELL,
    status: printStatus(appointment, now),
  };
}
